import React, { useContext, useState } from 'react';
import { Link as RouterLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  List,
  ListItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  CloudUpload as UploadIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  LibraryMusic as LibraryMusicIcon,
  PlayArrow as PlayIcon,
  Analytics as AnalyticsIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';
import { getProfileImageUrl } from '../../utils/urlHelpers';
import Footer from './Footer';
import Logo from '../common/Logo';

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Check if user is artist (either via accountType or isArtist for backward compatibility)
  const isArtist = user && (user.accountType === 'artist' || user.isArtist);
  // Check if user is curator
  const isCurator = user && user.accountType === 'curator';
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };
  
  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard', 
      auth: true,
      requireArtist: true // Show dashboard for artists only for now
    },
    { 
      text: 'Create New Release', 
      icon: <UploadIcon />, 
      path: '/upload/release', 
      auth: true,
      requireArtist: true // Only shown to artist accounts
    },
    { 
      text: 'Analytics', 
      icon: <AnalyticsIcon />, 
      path: '/artist/analytics', 
      auth: true,
      requireArtist: true // Only shown to artist accounts
    },
    { 
      text: 'Email Lists', 
      icon: <EmailIcon />, 
      path: '/email-lists', 
      auth: true,
      requireArtist: true // Only shown to artist accounts
    },
    { 
      text: 'My Library', 
      icon: <LibraryMusicIcon />, 
      path: '/curator/library', 
      auth: true,
      requireCurator: true // Only shown to curator accounts
    },
    { 
      text: 'Play Logs', 
      icon: <PlayIcon />, 
      path: '/curator/play-logs', 
      auth: true,
      requireCurator: true // Only shown to curator accounts
    }
  ];
  
  // Mobile drawer content
  const mobileDrawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-end',
        p: 2,
        pb: 1
      }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          pb: 2
        }}
      >
        <Logo 
          width={50} 
          color="#a187f2" 
          showText={true}
          textColor="#a187f2"
          fontSize="1.1rem"
          onClick={() => {
            navigate('/');
            handleDrawerToggle();
          }}
        />
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List>
        {menuItems.map((item) => (
          // Check all the requirements for each menu item
          (!item.auth || (item.auth && user)) && 
          (!item.requireArtist || isArtist) &&
          (!item.requireCurator || isCurator) && (
            <ListItem
              button
              key={item.text}
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: '4px',
                mx: 1,
                width: 'calc(100% - 16px)',
                mb: 0.5,                  '&.Mui-selected': {
                    backgroundColor: 'rgba(161, 135, 242, 0.16)',
                    '&:hover': {
                      backgroundColor: 'rgba(161, 135, 242, 0.24)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#a187f2',
                    },
                  },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: location.pathname === item.path ? '#a187f2' : 'inherit'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{ 
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(8px)',
          backgroundColor: '#1f1f21', // Dark grey for header
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          
          <Box 
            component={RouterLink} 
            to="/" 
            sx={{ 
              textDecoration: 'none',
              mr: 4,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Logo 
              width={40} 
              color="#a187f2" 
              showText={true}
              textColor="#a187f2"
              fontSize="1.25rem"
              onClick={() => navigate('/')} 
            />
          </Box>
          
          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {menuItems.map((item) => (
                (!item.auth || (item.auth && user)) && 
                (!item.requireArtist || isArtist) &&
                (!item.requireCurator || isCurator) && (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    sx={{ 
                      position: 'relative',
                      '&::after': location.pathname === item.path ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '30%',
                        height: '3px',
                        backgroundColor: '#a187f2',
                        borderRadius: '3px 3px 0 0'
                      } : {},
                      color: location.pathname === item.path ? '#a187f2' : '#e4e4e4'
                    }}
                  >
                    {item.text}
                  </Button>
                )
              ))}
            </Box>
          )}
          
          {/* Spacer to push profile to the right */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* User profile or login button */}
          {user ? (
            <Box>
              <IconButton 
                onClick={handleMenuOpen} 
                size="small"
                sx={{ 
                  border: '2px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    border: '2px solid rgba(120,188,237,0.5)',
                  }
                }}
              >
                <Avatar
                  src={getProfileImageUrl(user.profileImage)}
                  alt={user.username}
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    minWidth: '180px',
                    overflow: 'visible',
                    mt: 1.5,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                      borderLeft: '1px solid rgba(255,255,255,0.08)',
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                    },
                  },
                }}
              >
                <MenuItem 
                  component={RouterLink} 
                  to="/profile"
                  onClick={handleMenuClose}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" sx={{ color: '#a187f2' }} />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component={RouterLink}
              to="/login"
              startIcon={<LoginIcon />}
              sx={{ 
                color: '#e4e4e4',
                borderColor: 'rgba(228,228,228,0.5)',
                '&:hover': {
                  borderColor: '#e4e4e4',
                  backgroundColor: 'rgba(228,228,228,0.08)'
                }
              }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Mobile drawer */}
      <SwipeableDrawer
        anchor="left"
        open={isMobile && mobileOpen}
        onClose={handleDrawerToggle}
        onOpen={handleDrawerToggle}
        disableBackdropTransition={false}
        disableDiscovery={true}
        sx={{
          '& .MuiDrawer-paper': { 
            width: 280,
            backgroundColor: '#1f1f21' // Dark grey for drawer
          },
        }}
      >
        {mobileDrawer}
      </SwipeableDrawer>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: '56px', sm: '64px' },
          p: { xs: 2, sm: 3, md: 4 },
          width: '100%',
          backgroundColor: '#121212'
        }}
      >
        <Outlet />
      </Box>
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Layout;